import { useState, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { decodeQRFromImage, fileToDataURL } from '@/utils/qrImageDecoder';

export interface GalleryQRResult {
  success: boolean;
  data?: string;
  error?: string;
}

export function useGalleryQRScanner() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const resolveRef = useRef<((result: GalleryQRResult) => void) | null>(null);

  // Create hidden file input for web fallback
  const createFileInput = useCallback(() => {
    if (fileInputRef.current) {
      document.body.removeChild(fileInputRef.current);
    }
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';
    
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        resolveRef.current?.({ success: false, error: 'ไม่ได้เลือกรูปภาพ' });
        setIsLoading(false);
        return;
      }
      
      try {
        const dataUrl = await fileToDataURL(file);
        const result = await decodeQRFromImage(dataUrl);
        resolveRef.current?.(result);
      } catch (err) {
        console.error('File processing error:', err);
        resolveRef.current?.({ success: false, error: 'เกิดข้อผิดพลาดในการอ่านไฟล์' });
      } finally {
        setIsLoading(false);
      }
    };
    
    input.oncancel = () => {
      resolveRef.current?.({ success: false, error: 'ยกเลิกการเลือกรูปภาพ' });
      setIsLoading(false);
    };
    
    document.body.appendChild(input);
    fileInputRef.current = input;
    return input;
  }, []);

  // Pick image from gallery and decode QR
  const pickAndDecodeQR = useCallback(async (): Promise<GalleryQRResult> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if running on native platform
      const isNative = Capacitor.isNativePlatform();
      
      if (isNative) {
        // Use Capacitor Camera for native platforms
        try {
          const photo = await Camera.getPhoto({
            quality: 90,
            allowEditing: false,
            resultType: CameraResultType.DataUrl,
            source: CameraSource.Photos,
            promptLabelHeader: 'เลือกรูป QR Code',
            promptLabelPhoto: 'เลือกจากคลังรูปภาพ',
            promptLabelPicture: 'ถ่ายรูปใหม่',
          });
          
          if (photo.dataUrl) {
            const result = await decodeQRFromImage(photo.dataUrl);
            setIsLoading(false);
            
            if (!result.success) {
              setError(result.error || 'ไม่พบ QR Code');
            }
            
            return result;
          } else {
            setIsLoading(false);
            return { success: false, error: 'ไม่ได้รับรูปภาพ' };
          }
        } catch (err: any) {
          setIsLoading(false);
          // User cancelled
          if (err.message?.includes('cancelled') || err.message?.includes('User cancelled')) {
            return { success: false, error: 'ยกเลิกการเลือกรูปภาพ' };
          }
          console.error('Camera error:', err);
          return { success: false, error: 'ไม่สามารถเปิดคลังรูปภาพได้' };
        }
      } else {
        // Web fallback: use file input
        return new Promise((resolve) => {
          resolveRef.current = resolve;
          const input = createFileInput();
          input.click();
        });
      }
    } catch (err) {
      setIsLoading(false);
      console.error('Gallery QR error:', err);
      const errorMsg = 'เกิดข้อผิดพลาด';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [createFileInput]);

  // Cleanup on unmount
  const cleanup = useCallback(() => {
    if (fileInputRef.current) {
      document.body.removeChild(fileInputRef.current);
      fileInputRef.current = null;
    }
  }, []);

  return {
    pickAndDecodeQR,
    isLoading,
    error,
    cleanup,
  };
}
